import {
  type Observable,
  BehaviorSubject,
  share,
  scan,
  sampleTime,
  map,
} from 'rxjs';
import { exponentiallyWeightedAudioLevel } from './exponentiallyWeightedAudioLevel';

const AUDIO_LEVEL_INTERVAL_TIME = 300;

export type AudioLevelParser = (movingAvg: number, nextLevel: number) => number;

export type AudioLevelChanges = [string, number | undefined];

/**
 * Map of identityId to audioLevel (number between 0 and 100).
 */
export type AudioLevelsMap = ReadonlyMap<string, number>;

/**
 * This observes the audio levels across all subscribers (participants) currently on the call. There's a couple of steps which happen to allow us to get a legible volume the app is interested in
 * We firstly get the moving average - This logic is entirely ripped from Vonage based on their recommendation
 * @see https://developer.vonage.com/en/video/guides/ui-customization/general-customization#adjusting-user-interface-based-on-audio-levels
 * This runs every time we receive an update - ~60 per seconds per subscriber - e.g. 2 subscribers = 120 hits per second.
 * Future improvement could be to have it so we only process the audio every 100ms per subscriber (10 times per second per subscriber).
 * We cannot throttle in here as this runs across all subscribers which may results in losing audio updates for some subscribers. Any throttling has to be on a per subscriber basis
 * We then on a 100 ms interval calculate the actual volume - ensuring we've not missed off any audio updates we've received.
 * We apply further logic to calculate the volume again based on advice from Vonage and output a value from 0-100
 * @see https://developer.vonage.com/en/video/guides/ui-customization/general-customization#adjusting-user-interface-based-on-audio-levels
 */
export const observeAudioLevels = (
  audioLevelChanges: Observable<AudioLevelChanges>,
): Observable<AudioLevelsMap> => {
  return audioLevelChanges.pipe(
    scan((previousMap, [id, value]) => {
      const newMap = new Map(previousMap);

      if (value === undefined) {
        newMap.delete(id);
      } else {
        const previousAvg = newMap.get(id) ?? 0;
        const level = exponentiallyWeightedAudioLevel(previousAvg, value);
        newMap.set(id, level);
      }
      return newMap;
    }, new Map<string, number>()),

    /**
     * Emit the complete map on interval, instead of every time each participant has an audio level event.
     */
    sampleTime(AUDIO_LEVEL_INTERVAL_TIME),
    /**
     * Replace audioLevel from range [0,1] to [0, 100].
     */
    map((previousMap) => {
      return new Map(
        [...previousMap.entries()].map(([key, value]) => {
          // eslint-disable-next-line no-mixed-operators
          const logLevel = Math.log10(value) / 1.5 + 1;
          const volume = Math.min(Math.max(logLevel, 0), 1);
          const roundVolume = Math.round(volume * 100);

          return [key, roundVolume];
        }),
      );
    }),
    /**
     * Support any number of observers.
     * If an observer subscribes later on, immediately emit the current audio levels map.
     */
    share({
      connector: () => new BehaviorSubject<AudioLevelsMap>(new Map()),
    }),
  );
};
